import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractDialog, AbstractDialogData, AbstractDialogResultData, DialogComponent } from '@next/core-lib/dialog';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { CropAidFinalStatus, FertilizerOrderPlanResponse, OrderLine, Package, SiteState } from 'src/app/shared/models';

import { createFertilizerOrder } from '../../store/actions';
import { selectFertilizerOrderLines, selectFertilizerOrderPlan } from '../../store/selectors';

import { ConfirmOrderDialogOverviewComponent } from '../confirm-order-dialog/confirm-order-dialog.component';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatList, MatListItem, MatListItemIcon, MatListItemTitle, MatListItemLine } from '@angular/material/list';
import { TranslatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import { toNativeDate } from 'src/app/shared/utils';

export interface ConfirmOrderDialogDataFertilizer extends AbstractDialogData {
  fertilizerOrderPlan: FertilizerOrderPlanResponse | null;
  packages: Package[];
  cartLineCount: number;
}
@Component({
  selector: 'app-confirm-order-dialog-fertilizer',
  templateUrl: './confirm-order-dialog-fertilizer.component.html',
  styleUrl: './confirm-order-dialog-fertilizer.component.scss',
  imports: [
    DialogComponent,
    MatIcon,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatList,
    MatListItem,
    MatListItemIcon,
    MatListItemTitle,
    MatListItemLine,
    TranslatePipe,
    AgrNumberPipe,
  ],
})
export class ConfirmOrderDialogFertilizerComponent extends AbstractDialog implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  data = inject<ConfirmOrderDialogDataFertilizer>(MAT_DIALOG_DATA);
  protected readonly mdDialogRef = inject<MatDialogRef<ConfirmOrderDialogOverviewComponent, AbstractDialogResultData<never>>>(MatDialogRef);

  orderPlanWithShortage: CropAidFinalStatus[] = [];
  orderPlanWithExcess: CropAidFinalStatus[] = [];
  destroy$ = new Subject<boolean>();
  orderLines: OrderLine[] | undefined;
  customerOrderPlan: FertilizerOrderPlanResponse | null = null;
  lastDayOfNextYear = new Date(new Date().getFullYear() + 1, 11, 31);
  today = new Date();
  defaultDatepickerDate = this.twoWeeksFromNow();

  form = new FormGroup({
    deliveryDate: new FormControl(new Date(this.defaultDatepickerDate)),
  });

  ngOnInit(): void {
    this.selectOrderLines();
    this.selectFertilizerOrderPlan();
  }

  selectOrderLines(): void {
    this.store
      .select(selectFertilizerOrderLines)
      .pipe(takeUntil(this.destroy$))
      .subscribe((ol) => {
        this.orderLines = ol;
      });
  }

  selectFertilizerOrderPlan(): void {
    this.store
      .select(selectFertilizerOrderPlan)
      .pipe(takeUntil(this.destroy$))
      .subscribe((cop) => {
        this.customerOrderPlan = cop;
      });
  }

  getCropAid(id: number | undefined) {
    return this.data.fertilizerOrderPlan?.orderableProducts.find((product) => product.cropAidId === id);
  }

  getPackageCount(packageId: number): number {
    const line = this.data.fertilizerOrderPlan?.orderableProducts.find((p) => p.packageId === packageId);

    const totalQuantity = line?.amount ?? 0;
    const quantityPerBag = line?.minimumOrderUnitQuantity ?? 0;

    if (totalQuantity === 0 || quantityPerBag === 0) {
      return 0;
    }

    return totalQuantity / quantityPerBag;
  }

  getQuantityPerPackage(packageId: number): number {
    return (
      this.data.fertilizerOrderPlan?.orderableProducts.find((p) => p.packageId === packageId)
        ?.minimumOrderUnitQuantity ?? 0
    );
  }

  getPackageUnitOfMeasurement(packageId: number): string {
    return this.data.packages.find((p) => p.id === packageId)?.unitOfMeasurement ?? '';
  }

  confirm(): void {
    this.store.dispatch(createFertilizerOrder({ deliveryDate: toNativeDate(this.form.value.deliveryDate) }));
  }

  filterOutWeekends = (date: Date | null): boolean => {
    // Coerce to native Date to support MomentDateAdapter returning a Moment object from the datepicker filter
    const nativeDate = date ? new Date(+date) : new Date();
    const day = nativeDate.getDay();
    return day !== 0 && day !== 6;
  };

  twoWeeksFromNow(): Date {
    const date = new Date();
    date.setDate(date.getDate() + 15);
    return date;
  }

  showAmountWarning(): boolean {
    return !this.orderLines?.some((ol) => ol.status === 'Open' && ol.count > 0) ||
      !this.customerOrderPlan?.orderableProducts.some((p) => p.amount > 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
